
'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';


const AuthContext = createContext<any>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[AuthContext] Component is using useAuth() outside of AuthProvider.');
    }
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Email/Password Sign Up — creates account without auto-signing in
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        fullName: metadata?.fullName || '',
      }),
    });

    if (res.ok) {
      const result = await res.json();
      return result;
    }

    const serverResult = await res.json();
    if (res.status === 503) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.fullName || '',
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      // Sign out immediately so user must explicitly sign in
      await supabase.auth.signOut();
      return data;
    }

    throw new Error(serverResult.error || 'Signup failed');
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    
    // Check if user is admin and has 2FA enabled
    if (data.user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, totp_enabled')
        .eq('id', data.user.id)
        .maybeSingle();

      // If admin with 2FA enabled, check if device is trusted
      if (profile?.role === 'admin' && profile?.totp_enabled) {
        const deviceCheckResponse = await fetch('/api/auth/2fa/check-device', {
          method: 'POST'
        });
        const deviceCheck = await deviceCheckResponse.json();

        if (!deviceCheck.trusted) {
          // Return flag to show 2FA verification
          return { ...data, needsTwoFactor: true };
        }
      }
    }
    
    return data;
  };

  // OAuth Sign In (Google)
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  // Creates profile if it doesn't exist (fallback if trigger failed)
  const getUserProfile = async () => {
    if (!user) return null;
    
    // Try to get existing profile
    let { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    
    // If profile exists, return it
    if (data) return data;
    
    // If error is not "not found", throw it
    if (error && error.code !== 'PGRST116') throw error;
    
    // Profile doesn't exist - create it using upsert to handle race conditions
    // This will insert if not exists, or update if exists (handles concurrent requests)
    const fullName = user.user_metadata?.full_name || user.user_metadata?.fullName || user.email?.split('@')[0] || '';
    
    // Use upsert with onConflict to handle duplicate key errors gracefully
    const { data: newProfile, error: createError } = await supabase
      .from('user_profiles')
      .upsert({
        id: user.id,
        email: user.email || '',
        name: fullName,
        role: user.user_metadata?.role || 'buyer'
      }, {
        onConflict: 'id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (createError) {
      // If it's a duplicate key error (23505) or constraint violation, try to fetch again
      // This handles race conditions where profile was created between check and upsert
      if (createError.code === '23505' || createError.code === '23514') {
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        if (existingProfile) return existingProfile;
      }
      console.error('Error creating/updating user profile:', createError);
      throw createError;
    }
    
    return newProfile;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
